import { z } from 'zod';
import { PAGINATION } from '../../constants/index.js';
import { objectIdSchema, optionalSlugSchema } from '../common.js';
import { courseSchema, documentCategorySchema, documentSchema, droneShowPackageSchema, droneShowProjectSchema, newsCategorySchema, newsSchema, pageSchema, projectSchema, serviceSchema, staffSchema, } from './content.js';
/**
 * Entity sxemasidan ADMIN KIRISH sxemasini yasaydi.
 *
 * Olib tashlanadigan maydonlar — server o'zi qo'yadi va ularni tashqaridan
 * yozdirish mumkin emas:
 *   `_id`, vaqt belgilari, `createdBy`/`updatedBy` — texnik;
 *   `deletedAt` — o'chirish alohida endpoint orqali;
 *   `publishedAt` — nashr alohida endpoint orqali (`editor` nashr qila olmaydi);
 *   `legacy*` — faqat migratsiya (B12) yozadi.
 */
const AUDIT_OMIT = {
    _id: true,
    createdAt: true,
    updatedAt: true,
    createdBy: true,
    updatedBy: true,
    deletedAt: true,
};
const PUBLISH_OMIT = { isPublished: true, publishedAt: true };
const LEGACY_OMIT = { legacySource: true, legacyId: true, legacyUrl: true };
const CONTENT_OMIT = { ...AUDIT_OMIT, ...PUBLISH_OMIT };
const CONTENT_LEGACY_OMIT = { ...CONTENT_OMIT, ...LEGACY_OMIT };
export const createPageSchema = pageSchema.omit(CONTENT_LEGACY_OMIT);
export const createNewsSchema = newsSchema
    .omit(CONTENT_LEGACY_OMIT)
    .extend({ slug: optionalSlugSchema });
export const createStaffSchema = staffSchema
    .omit(CONTENT_LEGACY_OMIT)
    .extend({ slug: optionalSlugSchema });
export const createServiceSchema = serviceSchema
    .omit(CONTENT_LEGACY_OMIT)
    .extend({ slug: optionalSlugSchema });
export const createProjectSchema = projectSchema
    .omit(CONTENT_LEGACY_OMIT)
    .extend({ slug: optionalSlugSchema });
export const createDocumentSchema = documentSchema.omit(CONTENT_OMIT);
export const createCourseSchema = courseSchema
    .omit(CONTENT_OMIT)
    .extend({ slug: optionalSlugSchema });
export const createDroneShowPackageSchema = droneShowPackageSchema
    .omit(CONTENT_OMIT)
    .extend({ slug: optionalSlugSchema });
export const createDroneShowProjectSchema = droneShowProjectSchema
    .omit(CONTENT_OMIT)
    .extend({ slug: optionalSlugSchema });
// Kategoriyalarda nashr holati yo'q — ular har doim ko'rinadi.
export const createNewsCategorySchema = newsCategorySchema
    .omit(AUDIT_OMIT)
    .extend({ slug: optionalSlugSchema });
export const createDocumentCategorySchema = documentCategorySchema
    .omit(AUDIT_OMIT)
    .extend({ slug: optionalSlugSchema });
/**
 * Tahrirlash — barcha maydonlar ixtiyoriy.
 *
 * `.partial()` faqat BIRINCHI darajani ixtiyoriy qiladi: `title` berilsa,
 * uning ichidagi `uz` baribir majburiy. Bu ataylab — yarim to'ldirilgan
 * tarjima obyekti bazaga tushmasligi kerak.
 */
export const updatePageSchema = createPageSchema.partial();
export const updateNewsSchema = createNewsSchema.partial();
export const updateNewsCategorySchema = createNewsCategorySchema.partial();
export const updateStaffSchema = createStaffSchema.partial();
export const updateDocumentSchema = createDocumentSchema.partial();
export const updateDocumentCategorySchema = createDocumentCategorySchema.partial();
export const updateServiceSchema = createServiceSchema.partial();
export const updateProjectSchema = createProjectSchema.partial();
export const updateCourseSchema = createCourseSchema.partial();
export const updateDroneShowPackageSchema = createDroneShowPackageSchema.partial();
export const updateDroneShowProjectSchema = createDroneShowProjectSchema.partial();
// ─── Umumiy so'rov sxemalari ─────────────────────────────────────────────────
/** Admin ro'yxatlari: nashr holati bo'yicha ham filtrlanadi. */
export const adminListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).max(PAGINATION.MAX_PAGE).default(1),
    limit: z.coerce.number().int().min(1).max(PAGINATION.MAX_LIMIT).default(PAGINATION.DEFAULT_LIMIT),
    search: z.string().trim().max(200).optional(),
    isPublished: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    /** `true` — savatchadagi (o'chirilgan) yozuvlar */
    deleted: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    categoryId: objectIdSchema.optional(),
    sort: z.enum(['order', 'createdAt', 'title', 'publishedAt']).default('order'),
    direction: z.enum(['asc', 'desc']).default('asc'),
});
/** Nashr holatini o'zgartirish — alohida endpoint (`editor` qila olmaydi). */
export const publishSchema = z.object({
    isPublished: z.boolean(),
});
/** Elementlar tartibini almashtirish (drag&drop). */
export const reorderSchema = z.object({
    items: z
        .array(z.object({ _id: objectIdSchema, order: z.number().int().min(0) }))
        .min(1)
        .max(200),
});
// ─── Qoralama, qulf, ko'rish (TZ 7.2 §6–9) ──────────────────────────────────
/**
 * Avtosaqlash qoralamasi.
 *
 * Kirish TO'LIQ tekshirilmaydi — ataylab. Qoralama har 30 soniyada, xodim
 * yozib turgan paytda yuboriladi: sarlavha yarim yozilgan, slug hali bo'sh,
 * blok maydonlari to'ldirilmagan bo'lishi mumkin. Agar bu yerda `updateSchema`
 * qo'llansak, avtosaqlash aynan eng kerakli paytda — ish tugallanmaganda —
 * 422 qaytarardi va tiklash uchun hech narsa qolmasdi.
 *
 * Xavfsizlik: qoralama HECH QACHON saytga chiqmaydi. U faqat adminga va
 * `?preview=` orqali qaytadi, va o'sha yerda ham bloklar sanitizatsiyadan
 * o'tadi. Haqiqiy tekshiruv `PATCH :id` da — saqlashda.
 */
export const draftSchema = z.object({
    data: z.record(z.string(), z.unknown()),
});
export const lockSchema = z.object({
    /**
     * Qulfni tortib olish.
     *
     * Kerak, chunki muharrir brauzerni yopib qo'ysa qulf TTL tugagunicha
     * turadi. `admin`/`superadmin` kutib o'tirmasligi uchun majburan oladi —
     * amal jurnalga yoziladi.
     */
    force: z.boolean().default(false),
});
//# sourceMappingURL=inputs.js.map